import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined'
import {
	Box,
	Button,
	ButtonBase,
	Checkbox,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material'
import { ReactNode, useEffect, useMemo, useState } from 'react'

import { Loader } from '@/components/Loader/Loader'
import { LANGUAGE_KEY } from '@/context/Locale/Locale'
import { useAppContext } from '@/hooks'
import { MRDiffDrawer } from '@/pages/User/pages/MergeRequests/components/MRDiffDrawer'
import { DiffEngine } from '@/pages/User/pages/MergeRequests/diffEngine'
import {
	changeToMrStructure,
	changeToMrStructureFromHistories,
} from '@/pages/User/pages/MergeRequests/mapMergeRequest'
import type { MrStructure } from '@/pages/User/pages/MergeRequests/types'
import {
	computeSetDiff,
	filterSetDiffRows,
	SetDiffRow,
	structureCounts,
	unresolvedSetDiff,
} from '@/pages/User/pages/Releases/setDiff'
import {
	useGetBranchChangesBetweenQuery,
	useGetBranchChangesQuery,
	useLazyGetStructureHistoryByIdQuery,
} from '@/rtkApi'
import type {
	BranchStructureChangeDto,
	HistoryWithDeploymentDto,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import {
	getApiErrorDescription,
	logErrorAndNotifyWithToast,
} from '@/utils/errorHandling'

import {
	canCherryPickInto,
	canMarkCherryPickRow,
	cherryPickChangesArgs,
} from '../cherryPick'
import { toSetDiffFromChanges } from '../toSetDiffItems'
import { BranchRecord } from '../types'
import { CherryPickDialog } from './CherryPickDialog'

type BranchCompareViewProps = {
	from: BranchRecord
	onBack?: () => void
	/** A target the user picked explicitly is always compared as a pair, parent or not. */
	targetPickedByUser?: boolean
	/** Replaces the compared-branch name in the compare header. */
	targetSelect?: ReactNode
	to: BranchRecord
	variant?: 'cherry' | 'compare'
}

const BASE_TYPE_FILTERS = ['TABLE', 'VIEW', 'MAPPING', 'FOLDER']

const TYPE_LABEL_KEY: Partial<Record<string, LANGUAGE_KEY>> = {
	TABLE: 'TABLE',
	VIEW: 'VIEW',
	MAPPING: 'MAPPING',
	FOLDER: 'FOLDER',
	API_NODE: 'API_NODE',
	API_COLLECTION: 'API_COLLECTION',
	WORKFLOW: 'WORKFLOW',
	QUEUE: 'QUEUE',
	DIAGRAM: 'TAB_DIAGRAM',
	CONSTANT_DOMAIN: 'CONSTANT_DOMAIN',
}

const STATUS_FILTERS: {
	color: string
	key: string
	labelKey: LANGUAGE_KEY
	subKey?: LANGUAGE_KEY
}[] = [
	{
		key: 'ALL',
		labelKey: 'MERGE_REQUESTS_FILTER_ALL',
		color: '#265560',
		subKey: 'BRANCHES_COMPARE_IN_COMPARISON',
	},
	{ key: 'added', labelKey: 'MERGE_REQUESTS_ADDED', color: '#37B479' },
	{ key: 'removed', labelKey: 'MERGE_REQUESTS_REMOVED', color: '#DB433A' },
	{ key: 'modified', labelKey: 'MERGE_REQUESTS_MODIFIED', color: '#C77A1F' },
	{
		key: 'conflict',
		labelKey: 'BRANCHES_COMPARE_CONFLICT',
		color: '#DB433A',
		subKey: 'BRANCHES_COMPARE_NEEDS_RESOLUTION',
	},
]

const STATUS_TONE: Record<
	string,
	{ bg: string; color: string; labelKey: LANGUAGE_KEY }
> = {
	added: {
		bg: 'rgba(55,180,121,0.12)',
		color: '#1E8A55',
		labelKey: 'MERGE_REQUESTS_ADDED',
	},
	removed: {
		bg: 'rgba(219,67,58,0.12)',
		color: '#B3261E',
		labelKey: 'MERGE_REQUESTS_REMOVED',
	},
	modified: {
		bg: 'rgba(250,169,75,0.18)',
		color: '#9A5B00',
		labelKey: 'MERGE_REQUESTS_MODIFIED',
	},
	conflict: {
		bg: 'rgba(219,67,58,0.12)',
		color: '#B3261E',
		labelKey: 'BRANCHES_COMPARE_CONFLICT',
	},
}

const normalizeType = (type: string) => {
	const value = type.toUpperCase()
	if (value.startsWith('MAPPING')) return 'MAPPING'
	if (value === 'FOLDER_WORKFLOW') return 'FOLDER'
	return value
}

const TypeIcon = ({ type }: { type: string }) => {
	const key = normalizeType(type)
	const icon =
		key === 'VIEW' ? (
			<ViewAgendaOutlinedIcon sx={{ fontSize: 18 }} />
		) : key === 'MAPPING' ? (
			<AccountTreeOutlinedIcon sx={{ fontSize: 18 }} />
		) : key === 'FOLDER' ? (
			<FolderOutlinedIcon sx={{ fontSize: 18 }} />
		) : (
			<TableChartOutlinedIcon sx={{ fontSize: 18 }} />
		)
	return <Box sx={{ color: '#265560', display: 'flex' }}>{icon}</Box>
}

const headCellSx = {
	bgcolor: '#fff !important',
	color: 'rgba(0,0,0,0.87) !important',
	fontWeight: 500,
	fontSize: '0.875rem',
	borderBottom: '1px solid rgba(0,0,0,0.12)',
	py: 1.5,
}

export const BranchCompareView = ({
	from,
	onBack,
	targetPickedByUser = false,
	targetSelect,
	to,
	variant = 'compare',
}: BranchCompareViewProps) => {
	const { t } = useAppContext()
	const cherry = variant === 'cherry'
	const comparesToParent =
		!cherry && !targetPickedByUser && from.sourceBranchId === to.id
	const parentQuery = useGetBranchChangesQuery(
		{ branchId: Number(from.id) },
		{ skip: !comparesToParent },
	)
	const pairQuery = useGetBranchChangesBetweenQuery(
		cherry
			? cherryPickChangesArgs(Number(from.id), Number(to.id))
			: { sourceBranchId: Number(from.id), targetBranchId: Number(to.id) },
		{ skip: comparesToParent },
	)
	const { data, error, isError } = comparesToParent ? parentQuery : pairQuery
	const [getHistoryById] = useLazyGetStructureHistoryByIdQuery()
	const [diffStruct, setDiffStruct] = useState<MrStructure | null>(null)
	const [loadingStructureId, setLoadingStructureId] = useState<string | null>(
		null,
	)
	const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
		() => new Set(),
	)
	const [cherryPickOpen, setCherryPickOpen] = useState(false)
	const [statusFilter, setStatusFilter] = useState('ALL')
	const [typeFilters, setTypeFilters] = useState<string[]>([])
	const [query, setQuery] = useState('')
	const cherryPickEnabled = canCherryPickInto(from)
	const selectedStructureIds = useMemo(
		() => Array.from(selectedIds, (id) => Number(id)),
		[selectedIds],
	)

	useEffect(() => {
		setSelectedIds(new Set())
		setCherryPickOpen(false)
		setStatusFilter('ALL')
		setTypeFilters([])
		setQuery('')
	}, [from.id, to.id])

	useEffect(() => {
		if (!isError) return
		logErrorAndNotifyWithToast({
			context: { operation: 'BranchCompareView.loadChanges' },
			detail: getApiErrorDescription(error),
			error,
			messageKey: 'BRANCHES_COMPARE_LOAD_FAILED',
			t,
		})
	}, [error, isError, t])

	const { branchA, branchB } = useMemo(() => toSetDiffFromChanges(data), [data])
	const changesById = useMemo(() => {
		const map = new Map<string, BranchStructureChangeDto>()
		for (const change of data ?? []) {
			if (change.structureId != null) {
				map.set(String(change.structureId), change)
			}
		}
		return map
	}, [data])
	const diffResult = useMemo(
		() => computeSetDiff(branchA, branchB),
		[branchA, branchB],
	)
	const changedRows = useMemo(
		() => diffResult.rows.filter((row) => row.category !== 'unchanged'),
		[diffResult.rows],
	)
	const treeCache = useMemo(
		() =>
			({}) as Record<
				string,
				ReturnType<typeof DiffEngine.buildDiffTree> | null
			>,
		// eslint-disable-next-line react-hooks/exhaustive-deps -- cache resets when the compared sets change
		[branchA, branchB],
	)

	const structureDiff = (row: SetDiffRow) => {
		const change = changesById.get(row.structureId)
		return change ? changeToMrStructure(change).data : null
	}

	const diffTree = (row: SetDiffRow) => {
		if (row.category !== 'modified') return null
		if (!(row.structureId in treeCache)) {
			const snapshot = structureDiff(row)
			treeCache[row.structureId] = snapshot
				? DiffEngine.buildDiffTree(
						snapshot.base,
						snapshot.source,
						snapshot.target,
					)
				: null
		}
		return treeCache[row.structureId]
	}

	const fieldCounts = (row: SetDiffRow) => {
		const tree = diffTree(row)
		return tree ? structureCounts(tree) : null
	}

	const unresolvedCount = (row: SetDiffRow) => {
		const tree = diffTree(row)
		return tree ? unresolvedSetDiff(tree) : 0
	}

	const isConflict = (row: SetDiffRow) =>
		row.category === 'modified' && unresolvedCount(row) > 0

	const rowsForStatus = (status: string, source: SetDiffRow[]) => {
		if (status === 'ALL') return source
		if (status === 'conflict') return source.filter(isConflict)
		return source.filter((row) => row.category === status && !isConflict(row))
	}

	const typeFilterOptions = useMemo(() => {
		const present = new Set(changedRows.map((row) => normalizeType(row.type)))
		const extras = [...present]
			.filter((key) => !BASE_TYPE_FILTERS.includes(key))
			.sort()
		return ['ALL', ...BASE_TYPE_FILTERS, ...extras]
	}, [changedRows])
	const toggleTypeFilter = (key: string) => {
		if (key === 'ALL') {
			setTypeFilters([])
			return
		}
		setTypeFilters((current) =>
			current.includes(key)
				? current.filter((item) => item !== key)
				: [...current, key],
		)
	}
	const byType = useMemo(
		() =>
			typeFilters.length === 0
				? changedRows
				: changedRows.filter((row) =>
						typeFilters.includes(normalizeType(row.type)),
					),
		// Conflict counts are derived from diff trees that close over `data`.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- isConflict reads those trees
		[changedRows, typeFilters, data],
	)
	const byStatus = useMemo(
		() => rowsForStatus(statusFilter, changedRows),
		// Conflict rows are derived from diff trees that close over `data`.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- rowsForStatus reads those trees
		[changedRows, statusFilter, data],
	)
	const visibleRows = useMemo(() => {
		if (variant === 'cherry') return changedRows
		const matched = filterSetDiffRows(rowsForStatus(statusFilter, byType), {
			q: query,
		})
		if (statusFilter !== 'conflict') return matched
		return matched.filter(isConflict)
		// eslint-disable-next-line react-hooks/exhaustive-deps -- isConflict reads the diff trees
	}, [byType, changedRows, query, statusFilter, variant, data])

	const selectableRows = changedRows.filter(
		(row) => cherryPickEnabled && canMarkCherryPickRow(row.category),
	)
	const allPicked =
		selectableRows.length > 0 &&
		selectableRows.every((row) => selectedIds.has(row.structureId))
	const somePicked = selectedIds.size > 0 && !allPicked

	const toggleSelected = (structureId: string) => {
		setSelectedIds((current) => {
			const next = new Set(current)
			if (next.has(structureId)) next.delete(structureId)
			else next.add(structureId)
			return next
		})
	}

	const toggleAll = () => {
		setSelectedIds(
			allPicked
				? new Set()
				: new Set(selectableRows.map((row) => row.structureId)),
		)
	}

	const loadHistories = async (change: BranchStructureChangeDto) => {
		const found = new Map<number, HistoryWithDeploymentDto>()
		if (change.structureId == null) return found

		const structureId = change.structureId
		const wanted = new Set(
			[
				change.currentVersion,
				change.comparedVersion,
				change.ancestorVersion,
			].filter((id): id is number => id != null),
		)

		await Promise.all(
			Array.from(wanted, async (versionId) => {
				try {
					const history = await getHistoryById(
						{ structureId, versionId },
						true,
					).unwrap()
					found.set(history.id, history)
				} catch {
					// Left out on purpose: the reconstruction covers this side.
				}
			}),
		)
		return found
	}

	const openStructureDiff = async (change: BranchStructureChangeDto) => {
		if (change.structureId == null) return
		const structureId = String(change.structureId)
		setLoadingStructureId(structureId)
		try {
			const histories = await loadHistories(change)
			setDiffStruct(changeToMrStructureFromHistories(change, histories))
		} catch (historyError) {
			logErrorAndNotifyWithToast({
				context: { operation: 'BranchCompareView.loadHistorySnapshots' },
				detail: getApiErrorDescription(historyError),
				error: historyError,
				messageKey: 'BRANCHES_COMPARE_LOAD_FAILED',
				t,
			})
		} finally {
			setLoadingStructureId(null)
		}
	}

	const changeMarks = (row: SetDiffRow) => {
		if (row.category === 'added') {
			return [{ glyph: `+1`, color: '#1E8A55' }]
		}
		if (row.category === 'removed') {
			return [{ glyph: '−1', color: '#B3261E' }]
		}
		const counts = fieldCounts(row)
		if (!counts) return []
		return [
			counts.added ? { glyph: `+${counts.added}`, color: '#1E8A55' } : null,
			counts.modified
				? { glyph: `~${counts.modified}`, color: '#9A5B00' }
				: null,
			counts.removed ? { glyph: `−${counts.removed}`, color: '#B3261E' } : null,
		].filter((mark): mark is { color: string; glyph: string } => mark != null)
	}

	return (
		<Box
			sx={{
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				flex: 1,
				minHeight: 0,
			}}
			data-testid="branch-compare-view"
		>
			{onBack ? (
				<Button
					onClick={onBack}
					sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
				>
					{t('BACK')}
				</Button>
			) : null}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1.5,
					flexWrap: 'wrap',
					flexShrink: 0,
					pt: 0.75,
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
					{targetSelect}
					<ArrowForwardOutlinedIcon
						sx={{ fontSize: 18, color: 'rgba(0,0,0,0.6)' }}
					/>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 0.5,
							fontSize: 14,
							fontWeight: 600,
							color: '#1F4954',
						}}
					>
						<CallSplitOutlinedIcon sx={{ fontSize: 16 }} />
						{from.name}
					</Box>
				</Box>
				{variant === 'cherry' ? (
					<>
						<Box sx={{ flex: 1 }} />
						<Button
							variant="contained"
							disableElevation
							disabled={!cherryPickEnabled || selectedIds.size === 0}
							onClick={() => setCherryPickOpen(true)}
							data-testid="branch-cherry-pick"
							sx={{ textTransform: 'none' }}
						>
							{selectedIds.size
								? t('BRANCHES_CHERRY_PICK_COUNT', [String(selectedIds.size)])
								: t('BRANCHES_CHERRY_PICK')}
						</Button>
					</>
				) : null}
			</Box>

			{isError ? (
				<Typography
					data-testid="branch-compare-error"
					sx={{ fontSize: 14, color: 'text.secondary' }}
				>
					{t('BRANCHES_COMPARE_LOAD_FAILED')}
				</Typography>
			) : data == null ? (
				<Box sx={{ position: 'relative', height: 240 }}>
					<Loader loaded={false} $absolute />
				</Box>
			) : (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						flex: 1,
						minHeight: 0,
						overflow: 'hidden',
					}}
				>
					{variant === 'compare' ? (
						<>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
									border: '1px solid rgba(0,0,0,0.12)',
									borderRadius: '4px',
									overflow: 'hidden',
								}}
							>
								{STATUS_FILTERS.map((filter) => {
									const selected = statusFilter === filter.key
									const count =
										filter.key === 'ALL'
											? byType.length
											: filter.key === 'conflict'
												? byType.filter(isConflict).length
												: byType.filter(
														(row) =>
															row.category === filter.key && !isConflict(row),
													).length
									return (
										<ButtonBase
											key={filter.key}
											onClick={() => setStatusFilter(filter.key)}
											data-testid={`branch-compare-status-${filter.key}`}
											sx={{
												justifyContent: 'flex-start',
												gap: 1.25,
												px: 2,
												py: 1.5,
												borderRight: '1px solid rgba(0,0,0,0.12)',
												bgcolor: selected ? 'rgba(38,85,96,0.08)' : '#fff',
												boxShadow: selected ? 'inset 0 -2px 0 #265560' : 'none',
												'&:hover': { bgcolor: 'rgba(38,85,96,0.05)' },
											}}
										>
											<Typography
												sx={{
													fontSize: 22,
													fontWeight: 800,
													color: filter.color,
													lineHeight: 1,
												}}
											>
												{count}
											</Typography>
											<Box sx={{ textAlign: 'left', lineHeight: 1.15 }}>
												<Typography sx={{ fontWeight: 700, fontSize: 14 }}>
													{t(filter.labelKey)}
												</Typography>
												{filter.subKey ? (
													<Typography
														sx={{ fontSize: 11, color: 'text.secondary' }}
													>
														{t(filter.subKey)}
													</Typography>
												) : null}
											</Box>
										</ButtonBase>
									)
								})}
							</Box>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									flexWrap: 'wrap',
								}}
							>
								<Typography
									sx={{
										fontSize: 12,
										fontWeight: 500,
										letterSpacing: '0.08em',
										textTransform: 'uppercase',
										color: 'text.secondary',
										mr: 0.5,
									}}
								>
									{t('TYPE')}
								</Typography>
								{typeFilterOptions.map((key) => {
									const selected =
										key === 'ALL'
											? typeFilters.length === 0
											: typeFilters.includes(key)
									const count =
										key === 'ALL'
											? byStatus.length
											: byStatus.filter(
													(row) => normalizeType(row.type) === key,
												).length
									const labelKey =
										key === 'ALL'
											? 'MERGE_REQUESTS_FILTER_ALL'
											: TYPE_LABEL_KEY[key]
									return (
										<Chip
											key={key}
											label={`${labelKey ? t(labelKey) : key} ${count}`}
											onClick={() => toggleTypeFilter(key)}
											data-testid={`branch-compare-type-${key}`}
											sx={{
												height: 32,
												borderRadius: '16px',
												fontSize: 13,
												bgcolor: selected ? '#265560' : 'transparent',
												color: selected ? '#fff' : 'text.primary',
												border: '1px solid',
												borderColor: selected ? '#265560' : 'rgba(0,0,0,0.23)',
												'&:hover': {
													bgcolor: selected ? '#265560' : 'rgba(0,0,0,0.04)',
												},
											}}
										/>
									)
								})}
								<TextField
									size="small"
									label={t('SEARCH')}
									placeholder={t('BRANCHES_SEARCH_ELEMENTS')}
									value={query}
									onChange={(event) => setQuery(event.target.value)}
									data-testid="branch-compare-search"
									sx={{ width: 280, ml: 'auto' }}
								/>
							</Box>
						</>
					) : null}

					<TableContainer
						sx={{
							flex: 1,
							minHeight: 0,
							overflow: 'auto',
							border: '1px solid rgba(0,0,0,0.12)',
							borderRadius: '4px',
						}}
					>
						<Table
							stickyHeader
							size="small"
							sx={{
								borderCollapse: 'separate',
								'& .MuiTableHead-root .MuiTableRow-root': {
									backgroundColor: '#fff',
								},
								'& .MuiTableCell-head .MuiSvgIcon-root': {
									color: 'rgba(0,0,0,0.6) !important',
								},
							}}
						>
							<TableHead>
								<TableRow>
									{variant === 'cherry' ? (
										<TableCell padding="checkbox" sx={headCellSx}>
											<Checkbox
												size="small"
												checked={allPicked}
												indeterminate={somePicked}
												disabled={selectableRows.length === 0}
												onChange={toggleAll}
												data-testid="branch-cherry-pick-all"
											/>
										</TableCell>
									) : null}
									<TableCell sx={headCellSx}>{t('BRANCHES_ELEMENT')}</TableCell>
									<TableCell sx={headCellSx}>{t('BRANCHES_CODE')}</TableCell>
									<TableCell sx={{ ...headCellSx, width: 120 }}>
										{t('BRANCHES_STATUS')}
									</TableCell>
									{variant === 'compare' ? (
										<>
											<TableCell sx={{ ...headCellSx, width: 120 }}>
												{t('BRANCHES_CHANGES')}
											</TableCell>
											<TableCell sx={{ ...headCellSx, width: 90 }} />
										</>
									) : null}
								</TableRow>
							</TableHead>
							<TableBody>
								{visibleRows.length === 0 ? (
									<TableRow>
										<TableCell
											colSpan={variant === 'cherry' ? 4 : 5}
											sx={{
												py: 3,
												textAlign: 'center',
												color: 'text.secondary',
											}}
										>
											{changedRows.length === 0
												? t('BRANCHES_COMPARE_NO_DIFF')
												: t('BRANCHES_COMPARE_NO_MATCH')}
										</TableCell>
									</TableRow>
								) : (
									visibleRows.map((row) => {
										const tone =
											STATUS_TONE[isConflict(row) ? 'conflict' : row.category]
										const marks = changeMarks(row)
										const canSelect =
											cherryPickEnabled && canMarkCherryPickRow(row.category)
										const picked = selectedIds.has(row.structureId)
										const openDiff = () => {
											const change = changesById.get(row.structureId)
											if (change) void openStructureDiff(change)
										}
										return (
											<TableRow
												key={row.structureId}
												hover
												onClick={variant === 'compare' ? openDiff : undefined}
												data-testid={`branch-compare-row-${row.structureId}`}
												sx={{
													bgcolor: picked ? 'rgba(38,85,96,0.08)' : '#fff',
													cursor: variant === 'compare' ? 'pointer' : 'default',
												}}
											>
												{variant === 'cherry' ? (
													<TableCell padding="checkbox">
														<Checkbox
															size="small"
															checked={picked}
															disabled={!canSelect}
															onChange={() => toggleSelected(row.structureId)}
															data-testid={`branch-cherry-pick-row-${row.structureId}`}
														/>
													</TableCell>
												) : null}
												<TableCell>
													<Box
														sx={{
															display: 'flex',
															alignItems: 'center',
															gap: 1,
															fontWeight: 600,
														}}
													>
														<TypeIcon type={row.type} />
														{row.name}
													</Box>
												</TableCell>
												<TableCell
													sx={{
														fontFamily:
															'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
														fontSize: 12,
														color: 'text.secondary',
													}}
												>
													{row.code}
												</TableCell>
												<TableCell>
													{tone ? (
														<Box
															component="span"
															sx={{
																fontSize: 10,
																fontWeight: 700,
																letterSpacing: '0.06em',
																textTransform: 'uppercase',
																borderRadius: '6px',
																px: '7px',
																py: '2px',
																color: tone.color,
																bgcolor: tone.bg,
															}}
														>
															{t(tone.labelKey)}
														</Box>
													) : null}
												</TableCell>
												{variant === 'compare' ? (
													<>
														<TableCell>
															<Box
																sx={{
																	display: 'flex',
																	gap: 1,
																	fontSize: 12.5,
																	fontWeight: 700,
																}}
															>
																{marks.map((mark) => (
																	<Box
																		key={mark.glyph}
																		component="span"
																		sx={{ color: mark.color }}
																	>
																		{mark.glyph}
																	</Box>
																))}
															</Box>
														</TableCell>
														<TableCell align="right">
															<Button
																size="small"
																onClick={(event) => {
																	event.stopPropagation()
																	openDiff()
																}}
																data-testid={`branch-compare-diff-${row.structureId}`}
															>
																{t('BRANCHES_DIFF')}
															</Button>
														</TableCell>
													</>
												) : null}
											</TableRow>
										)
									})
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			)}
			{loadingStructureId ? <Loader loaded={false} $absolute /> : null}
			<MRDiffDrawer
				open={Boolean(diffStruct)}
				structure={diffStruct}
				readOnly
				sourceBranchName={from.name}
				targetBranchName={to.name}
				onClose={() => setDiffStruct(null)}
			/>
			<CherryPickDialog
				isOpen={cherryPickOpen}
				onClose={() => setCherryPickOpen(false)}
				onFinished={() => setSelectedIds(new Set())}
				sourceBranchId={Number(to.id)}
				sourceBranchName={to.name}
				structureIds={selectedStructureIds}
				targetBranchId={Number(from.id)}
				targetBranchName={from.name}
			/>
		</Box>
	)
}
